/**
 * Free Card Image Helper
 * Provides card logos using free APIs that support all card types worldwide
 * No rate limits, no API keys required
 */
import creditCardType from "credit-card-type";

/**
 * Card type to image URL mapping using FREE colorful image sources
 * All images are colorful PNG/JPG - no black/white icons
 * Supports all major card types worldwide
 * Using logos-world.net and other free CDNs for colorful brand logos
 */
const CARD_IMAGE_MAP = {
  // Visa - Colorful blue logo (PNG)
  visa: "https://logos-world.net/wp-content/uploads/2020/04/Visa-Logo.png",
  // Mastercard - Colorful red/orange circles (PNG)
  mastercard: "https://logos-world.net/wp-content/uploads/2020/09/Mastercard-Logo.png",
  // American Express - Colorful blue logo (PNG)
  "american-express": "https://logos-world.net/wp-content/uploads/2020/09/American-Express-Logo.png",
  amex: "https://logos-world.net/wp-content/uploads/2020/09/American-Express-Logo.png",
  "americanexpress": "https://logos-world.net/wp-content/uploads/2020/09/American-Express-Logo.png",
  // Discover - Colorful orange logo (PNG)
  discover: "https://logos-world.net/wp-content/uploads/2020/09/Discover-Logo.png",
  // Diners Club - Colorful logo (PNG from Wikipedia)
  "diners-club": "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Diners_Club_International_logo.svg/800px-Diners_Club_International_logo.svg.png",
  dinersclub: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Diners_Club_International_logo.svg/800px-Diners_Club_International_logo.svg.png",
  diners: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Diners_Club_International_logo.svg/800px-Diners_Club_International_logo.svg.png",
  // JCB - Colorful logo (PNG) - using logos-world for better color
  jcb: "https://logos-world.net/wp-content/uploads/2020/09/JCB-Logo.png",
  // UnionPay - Colorful red logo (PNG from Wikipedia)
  "unionpay": "https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/UnionPay_logo.svg/800px-UnionPay_logo.svg.png",
  union: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/UnionPay_logo.svg/800px-UnionPay_logo.svg.png",
  // Maestro - Colorful logo - using Mastercard official source for better color
  maestro: "https://www.mastercard.com/content/dam/mccom/global/logos/logo-maestro-mobile.svg",
  // PayPal - Colorful blue/yellow logo (PNG)
  paypal: "https://logos-world.net/wp-content/uploads/2020/06/PayPal-Logo.png",
};

/**
 * Alternative free colorful image sources (fallback) - Multiple colorful PNG/JPG sources
 * All are colorful images, prioritized PNG/JPG over SVG for better color rendering
 */
const FREE_IMAGE_SOURCES = {
  visa: [
    "https://www.visa.com/dam/VCOM/regional/na/usa/pay-with-visa/images/visa-logo-800x450.jpg",
    "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/800px-Visa_Inc._logo.svg.png",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/visa_logo_blue.png"
  ],
  mastercard: [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/800px-Mastercard-logo.svg.png",
    "https://www.mastercard.com/content/dam/mccom/global/logos/logo-mastercard-mobile.svg",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/mastercard_logo.png"
  ],
  amex: [
    "https://www.americanexpress.com/content/dam/amex/us/staticassets/pdf/LogoFiles/Amex_logo_centered_blue_RGB_72dpi.png",
    "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/800px-American_Express_logo_%282018%29.svg.png",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/amex_logo.png"
  ],
  discover: [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/Discover_Card_logo.svg/800px-Discover_Card_logo.svg.png",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/discover_logo.png"
  ],
  jcb: [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/JCB_logo.svg/800px-JCB_logo.svg.png",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/jcb_logo.png"
  ],
  maestro: [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/Maestro_2016.svg/800px-Maestro_2016.svg.png",
    "https://www.mastercard.com/content/dam/mccom/global/logos/logo-maestro-mobile.svg"
  ],
  "diners-club": [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Diners_Club_International_logo.svg/800px-Diners_Club_International_logo.svg.png",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/diners_club_logo.png"
  ],
  unionpay: [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/UnionPay_logo.svg/800px-UnionPay_logo.svg.png",
    "https://cdn.brandfolder.io/KGT0W8QX/at/8xqj9x9x9x9x9x9x9x9x9x/unionpay_logo.png"
  ],
  paypal: [
    "https://logos-world.net/wp-content/uploads/2020/06/PayPal-Logo.png",
    "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/800px-PayPal.svg.png"
  ]
};

/**
 * Get all alternative image URLs for a card type (for fallback)
 */
const getAlternativeImageUrls = (normalizedType) => {
  if (!normalizedType) return [];
  
  const alternatives = FREE_IMAGE_SOURCES[normalizedType];
  return alternatives || [];
};

/**
 * Normalize card type string to match our mapping
 * @param {string} cardType - The card type string
 * @returns {string} Normalized card type
 */
const normalizeCardType = (cardType) => {
  if (!cardType) return null;
  
  // Convert to lowercase and remove special characters
  let normalized = cardType
    .toLowerCase()
    .replace(/[-\s_]/g, "-")
    .trim();
  
  // Ignore generic types that don't indicate brand
  if (normalized === "debit" || normalized === "credit" || normalized === "prepaid") {
    return null; // Return null so we can detect from card number instead
  }
  
  // Handle common variations
  const variations = {
    "americanexpress": "american-express",
    "amex": "american-express",
    "dinersclub": "diners-club",
    "diners": "diners-club",
    "union": "unionpay",
  };
  
  return variations[normalized] || normalized;
};

/**
 * Get card image URL using free APIs
 * Supports all card types worldwide without any API limits
 * 
 * @param {string} cardType - The card type (e.g., "visa", "mastercard", "amex")
 * @param {string} cardNumber - Optional card number for detection
 * @returns {string} Card image URL or null
 */
export const getFreeCardImage = (cardType = null, cardNumber = null) => {
  try {
    let normalizedType = null;
    
    // If card type is provided, try to normalize it first
    if (cardType) {
      normalizedType = normalizeCardType(cardType);
    }
    
    // Always try to detect from card number if available (more reliable)
    // This ensures we get the correct brand even if API returns generic "CREDIT"/"DEBIT"
    // If card number is provided, try to detect from number (even if we have a type)
    // This works even with masked numbers like "4XXXXXXXXXXX4242"
    if (cardNumber) {
      const sanitizedNumber = cardNumber.replace(/\D/g, "");
      
      // Need at least first digit to detect
      if (sanitizedNumber.length > 0) {
        const firstDigit = sanitizedNumber[0];
        let detectedType = null;
        
        // Enhanced BIN detection for all card types
        if (firstDigit === "4") {
          detectedType = "visa";
        } else if (firstDigit === "5" || firstDigit === "2") {
          // Mastercard: starts with 2 or 5
          if (sanitizedNumber.length >= 2) {
            const firstTwo = sanitizedNumber.substring(0, 2);
            // Maestro cards start with 50, 56-69
            if (firstTwo >= "50" && firstTwo <= "69") {
              detectedType = "maestro";
            } else {
              detectedType = "mastercard";
            }
          } else {
            detectedType = "mastercard";
          }
        } else if (firstDigit === "3") {
          // Could be Amex, JCB, or Diners Club - need more digits
          if (sanitizedNumber.length >= 2) {
            const firstTwo = sanitizedNumber.substring(0, 2);
            // JCB starts with 35
            if (firstTwo === "35") {
              detectedType = "jcb";
            } 
            // Diners Club starts with 30, 36, 38
            else if (firstTwo === "30" || firstTwo === "36" || firstTwo === "38") {
              detectedType = "diners-club";
            }
            // Amex starts with 34 or 37
            else if (firstTwo === "34" || firstTwo === "37") {
              detectedType = "american-express";
            } else {
              // Default to Amex for other 3x patterns
              detectedType = "american-express";
            }
          } else {
            detectedType = "american-express";
          }
        } else if (firstDigit === "6") {
          // Could be Discover or Maestro
          if (sanitizedNumber.length >= 2) {
            const firstTwo = sanitizedNumber.substring(0, 2);
            // Maestro cards can also start with 60-69
            if (firstTwo >= "60" && firstTwo <= "69") {
              detectedType = "maestro";
            } 
            // Discover patterns: 6011, 622126-622925, 644-649, 65
            else if (sanitizedNumber.length >= 4) {
              const firstFour = sanitizedNumber.substring(0, 4);
              if (firstFour === "6011" || 
                  (firstFour >= "6221" && firstFour <= "6229") ||
                  (firstFour >= "6440" && firstFour <= "6499")) {
                detectedType = "discover";
              } else if (firstTwo === "65") {
                detectedType = "discover";
              } else {
                detectedType = "discover";
              }
            } else {
              detectedType = "discover";
            }
          } else {
            detectedType = "discover";
          }
        }
        
        // Use detected type from card number if we got one (more reliable than API type)
        if (detectedType) {
          normalizedType = detectedType;
        }
      }
    }
    
    // Return mapped image if found (primary source)
    if (normalizedType && CARD_IMAGE_MAP[normalizedType]) {
      return CARD_IMAGE_MAP[normalizedType];
    }
    
    // Try first alternative source as fallback
    if (normalizedType && FREE_IMAGE_SOURCES[normalizedType] && FREE_IMAGE_SOURCES[normalizedType].length > 0) {
      return FREE_IMAGE_SOURCES[normalizedType][0];
    }
    
    return null;
  } catch (error) {
    console.error("Error getting free card image:", error);
    return null;
  }
};

/**
 * Get card image URL asynchronously (for compatibility with existing code)
 * Uses free APIs - no rate limits, no API keys
 * 
 * @param {string} cardType - The card type
 * @param {string} cardNumber - Optional card number
 * @returns {Promise<string>} Card image URL or null
 */
export const getFreeCardImageAsync = async (cardType = null, cardNumber = null) => {
  return new Promise((resolve) => {
    try {
      const imageUrl = getFreeCardImage(cardType, cardNumber);
      resolve(imageUrl);
    } catch (error) {
      console.error("Error in async card image fetch:", error);
      resolve(null);
    }
  });
};

/**
 * Get card image from card number using credit-card-type library
 * This is a drop-in replacement for the Clearbit API
 * 
 * @param {string} cardNumber - The card number
 * @returns {Promise<string>} Card image URL or null
 */
export const getCardImageFromNumber = async (cardNumber) => {
  try {
    if (!cardNumber) return null;
    
    const sanitizedValue = cardNumber.replace(/\D/g, "");
    
    // Need at least 1 digit to detect (works even with masked numbers)
    if (sanitizedValue.length < 1) return null;
    
    // First try direct detection from number (fastest, most reliable)
    const directImage = getFreeCardImage(null, sanitizedValue);
    if (directImage) return directImage;
    
    // If direct detection didn't work, try credit-card-type library
    if (sanitizedValue.length >= 4) {
      try {
        const cardType = creditCardType(sanitizedValue)[0];
        
        if (cardType && cardType.type) {
          const imageUrl = getFreeCardImage(cardType.type, sanitizedValue);
          return imageUrl;
        }
      } catch (libError) {
        // Library might not be available, continue with direct detection
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error getting card image from number:", error);
    return null;
  }
};

/**
 * Get all alternative image URLs for a card type (for error fallback)
 * @param {string} cardType - The card type
 * @param {string} cardNumber - Optional card number for detection
 * @returns {Array<string>} Array of alternative image URLs
 */
export const getAlternativeCardImages = (cardType = null, cardNumber = null) => {
  try {
    let normalizedType = null;
    
    // If card type is provided, use it
    if (cardType) {
      normalizedType = normalizeCardType(cardType);
    }
    
    // If card number is provided and no valid type, try to detect from number
    if (!normalizedType && cardNumber) {
      const sanitizedNumber = cardNumber.replace(/\D/g, "");
      if (sanitizedNumber.length > 0) {
        const firstDigit = sanitizedNumber[0];
        if (firstDigit === "4") {
          normalizedType = "visa";
        } else if (firstDigit === "5" || firstDigit === "2") {
          if (sanitizedNumber.length >= 2) {
            const firstTwo = sanitizedNumber.substring(0, 2);
            if (firstTwo >= "50" && firstTwo <= "69") {
              normalizedType = "maestro";
            } else {
              normalizedType = "mastercard";
            }
          } else {
            normalizedType = "mastercard";
          }
        } else if (firstDigit === "3") {
          if (sanitizedNumber.length >= 2) {
            const firstTwo = sanitizedNumber.substring(0, 2);
            if (firstTwo === "35") {
              normalizedType = "jcb";
            } else if (firstTwo === "30" || firstTwo === "36" || firstTwo === "38") {
              normalizedType = "diners-club";
            } else if (firstTwo === "34" || firstTwo === "37") {
              normalizedType = "american-express";
            } else {
              normalizedType = "american-express";
            }
          } else {
            normalizedType = "american-express";
          }
        } else if (firstDigit === "6") {
          if (sanitizedNumber.length >= 2) {
            const firstTwo = sanitizedNumber.substring(0, 2);
            if (firstTwo >= "60" && firstTwo <= "69") {
              normalizedType = "maestro";
            } else if (sanitizedNumber.length >= 4) {
              const firstFour = sanitizedNumber.substring(0, 4);
              if (firstFour === "6011" || 
                  (firstFour >= "6221" && firstFour <= "6229") ||
                  (firstFour >= "6440" && firstFour <= "6499")) {
                normalizedType = "discover";
              } else if (firstTwo === "65") {
                normalizedType = "discover";
              } else {
                normalizedType = "discover";
              }
            } else {
              normalizedType = "discover";
            }
          } else {
            normalizedType = "discover";
          }
        }
      }
    }
    
    if (!normalizedType) return [];
    
    // Return all alternative URLs for this card type
    return getAlternativeImageUrls(normalizedType);
  } catch (error) {
    console.error("Error getting alternative card images:", error);
    return [];
  }
};

const cardImageHelper = {
  getFreeCardImage,
  getFreeCardImageAsync,
  getCardImageFromNumber,
  getAlternativeCardImages,
  normalizeCardType,
};

export default cardImageHelper;
